import { Link } from 'react-router-dom'
import { useHead, useSeoMeta } from '@unhead/react'
import {Zap, GraduationCap, BookOpen, ArrowRight} from 'lucide-react'
import { track } from '@/lib/analytics'

const features = [
  {
    icon: Zap,
    label: '01',
    title: 'Speed & Accuracy',
    body: 'Learn how to avoid common mental math errors and improve your calculator basic math response time on timed exams.',
  },
  {
    icon: GraduationCap,
    label: '02',
    title: 'Exam Preparation',
    body: 'Specialized tools designed for educators and students — including a Praxis Core calculator emulator that mirrors official testing environments.',
  },
  {
    icon: BookOpen,
    label: '03',
    title: 'Pedagogical Support',
    body: 'Resources and guides for teaching calculator skills to elementary students, with clear progression from K through 8.',
  },
]

export default function Home() {
  useSeoMeta({
    title: 'Calculator Skills for Basic Math | CalculatorSkills',
    description: 'Master calculator skills and basic math computation with free online tools, the Praxis Core calculator emulator, and lessons for students and educators.',
    ogTitle: 'Calculator Skills for Basic Math | CalculatorSkills',
    ogDescription: 'Free tools and tutorials to master calculator skills and basic math computation.',
    ogImage: 'https://www.calculatorskills.com/og/home.jpg',
    ogUrl: 'https://www.calculatorskills.com/',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Calculator Skills for Basic Math',
    twitterDescription: 'Free online tools to master calculator skills and computation skills in math.',
    twitterImage: 'https://www.calculatorskills.com/og/home.jpg',
  })
  useHead({
    link: [{ rel: 'canonical', href: 'https://www.calculatorskills.com/' }],
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'CalculatorSkills',
          url: 'https://www.calculatorskills.com/',
          description: 'Free online tools and tutorials to master calculator skills and basic math computation.',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://www.calculatorskills.com/blog?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }),
      },
    ],
  })

  return (
    <div className="bg-[#FAFAF7]">
      {/* HERO */}
      <section className="relative overflow-hidden border-b-2 border-[#0A0A0A]">
        <div className="absolute inset-0 swiss-grid opacity-60 pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7 reveal">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-12 h-[2px] bg-[#E63946]" />
                <p className="eyebrow">/ Issue 01 — 2025</p>
              </div>
              <h1 className="headline text-[44px] sm:text-[64px] lg:text-[88px] mb-8">
                Improve Your{' '}<br/>
                Calculator Skills{' '}<br/>
                for <span className="text-[#E63946] italic font-light">Basic Math.</span>
              </h1>
              <p className="text-lg lg:text-xl text-[#475569] max-w-[560px] leading-relaxed mb-10">
                Free online tools and tutorials to master math computation skills, featuring our specialized exam-prep calculators.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/calculator"
                  onClick={() => track('cta_click', '/', { cta: 'try_calculator_hero' })}
                  className="btn-primary"
                >
                  Try the Free Calculator
                  <ArrowRight size={18} />
                </Link>
                <Link to="/blog" className="btn-secondary">
                  Read the Blog
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 reveal" style={{ animationDelay: '120ms' }}>
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-[#E63946]" aria-hidden="true" />
                <img
                  src="https://static.lumi.new/cd/cda0a1b618bfac8495633601708b005c.png"
                  alt="Illustration of an online basic math calculator with mathematical equations, demonstrating calculator skills for students"
                  className="relative w-full border-2 border-[#0A0A0A] aspect-[4/3] object-cover"
                  loading="eager"
                />
                <div className="absolute -bottom-4 -right-4 bg-[#0A0A0A] text-[#FAFAF7] px-4 py-2 font-mono text-xs tracking-wider">
                  PEMDAS · 8-DIGIT · 4-FN
                </div>
              </div>
            </div>
          </div>

          {/* meta strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-[#0A0A0A]/15">
            {[
              { k: 'Free tools', v: '100%' },
              { k: 'Calculators', v: '01' },
              { k: 'Articles', v: '02' },
              { k: 'Audience', v: 'K–12 + Adult' },
            ].map((m) => (
              <div key={m.k}>
                <p className="font-mono text-[11px] tracking-widest text-[#475569] uppercase mb-1">{m.k}</p>
                <p className="font-extrabold text-2xl tracking-tight">{m.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-b-2 border-[#0A0A0A]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-4">
              <p className="eyebrow text-[#E63946] mb-4">/ 02 Why CalculatorSkills</p>
              <h2 className="headline text-4xl lg:text-5xl">
                Three principles<br/>for better<br/>computation.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <p className="text-[#475569] text-lg leading-relaxed">
                Whether you're preparing for the Praxis Core, teaching a fifth-grade class, or refreshing your own arithmetic, the same three principles apply. We build every tool and article around them.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-[#0A0A0A]">
            {features.map((f, idx) => (
              <article
                key={f.title}
                className={`p-8 lg:p-10 ${idx < 2 ? 'md:border-r-2 border-[#0A0A0A]' : ''} ${idx > 0 ? 'border-t-2 md:border-t-0 border-[#0A0A0A]' : ''} hover:bg-[#0A0A0A] hover:text-[#FAFAF7] transition-colors group`}
              >
                <div className="flex items-start justify-between mb-8">
                  <f.icon size={28} strokeWidth={1.5} />
                  <span className="font-mono text-xs tracking-widest opacity-60">{f.label}</span>
                </div>
                <h3 className="headline text-2xl mb-3">{f.title}</h3>
                <p className="text-[#475569] group-hover:text-[#FAFAF7]/70 leading-relaxed text-[15px]">{f.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-[#0A0A0A] text-[#FAFAF7]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="eyebrow text-[#E63946] mb-4">/ 03 Try it now</p>
              <h2 className="headline text-4xl lg:text-6xl mb-6">
                The Praxis Core<br/>Calculator Emulator.
              </h2>
              <p className="text-[#FAFAF7]/60 text-lg max-w-xl leading-relaxed">
                A four-function basic math calculator online, built to mirror the standardized testing environment. Strict PEMDAS order of operations, memory keys, 8-digit display, and a transfer-display button.
              </p>
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <Link
                to="/calculator"
                onClick={() => track('cta_click', '/', { cta: 'try_calculator_strip' })}
                className="inline-flex items-center gap-3 bg-[#E63946] hover:bg-[#FAFAF7] hover:text-[#0A0A0A] text-white px-8 py-5 font-semibold tracking-tight transition-colors"
              >
                Open the Calculator
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
